import React from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Card,
  Col,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ExclamationCircleOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  ViolationType,
  WatchlistEvent,
  WatchlistUser,
  useWatchlist,
} from '@shared/api/hooks/useWatchlist';
import { brand } from '@/theme/tokens';

// ---------------------------------------------------------------------------
// Costanti visive per tipo di violazione
// ---------------------------------------------------------------------------

const VIOLATION_COLOR: Record<ViolationType, string> = {
  AI_GENERATED: brand.primaryDeep,    // #7A0000 — rosso scuro — prova quasi certa
  SUSPECTED_FORGERY: brand.danger,    // #d42027 — rosso — scontrino artefatto
  DISPARITY_FLAG: brand.warn,         // #e8911a — arancio — disparità R/V
};

const VIOLATION_LABEL: Record<ViolationType, string> = {
  AI_GENERATED: 'AI generato',
  SUSPECTED_FORGERY: 'Falso sospetto',
  DISPARITY_FLAG: 'Disparità R/V',
};

const VIOLATION_TOOLTIP: Record<ViolationType, string> = {
  AI_GENERATED:
    'I metadati C2PA o il pHash confermano che l\'immagine è generata/alterata da AI. Prova quasi certa di frode.',
  SUSPECTED_FORGERY:
    'Il semaforo di veridicità (V) è rosso: lo scontrino ha bassa autenticità visiva/metadati. Probabile artefatto.',
  DISPARITY_FLAG:
    'Alta disparità tra leggibilità (R alto) e veridicità (V basso): scontrino probabilmente costruito digitalmente.',
};

// ---------------------------------------------------------------------------
// Chip tipo violazione
// ---------------------------------------------------------------------------

function ViolationChip({ type }: { type: ViolationType }): React.ReactElement {
  return (
    <Tooltip title={VIOLATION_TOOLTIP[type]}>
      <Tag
        color={VIOLATION_COLOR[type]}
        style={{ fontWeight: 700, cursor: 'help' }}
      >
        {VIOLATION_LABEL[type]}
      </Tag>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Tabella tifosi a rischio
// ---------------------------------------------------------------------------

const FLAGGED_COLUMNS = [
  {
    title: 'Tifoso',
    key: 'user',
    render: (_v: unknown, r: WatchlistUser) => (
      <Space direction="vertical" size={0}>
        <Typography.Text strong>
          {r.displayName ?? <Typography.Text type="secondary">—</Typography.Text>}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {r.email}
        </Typography.Text>
      </Space>
    ),
  },
  {
    title: 'Livello rischio',
    dataIndex: 'riskLevel',
    width: 130,
    render: (v: WatchlistUser['riskLevel']) =>
      v === 'HIGH' ? (
        <Tag color={brand.danger} style={{ fontWeight: 800 }}>
          HIGH
        </Tag>
      ) : (
        <Tag color="default">NORMAL</Tag>
      ),
  },
  {
    title: (
      <Tooltip title="Score di fiducia 0-100. Più è basso, più il tifoso è sospetto.">
        Trust score
      </Tooltip>
    ),
    dataIndex: 'trustScore',
    width: 110,
    align: 'right' as const,
    render: (v: number) => (
      <Typography.Text
        strong
        style={{
          color: v < 40 ? brand.danger : v < 60 ? brand.warn : brand.win,
        }}
      >
        {v} / 100
      </Typography.Text>
    ),
  },
  {
    title: (
      <Tooltip title="Numero di eventi sospetti (DISPARITY_FLAG, AI_GENERATED, SUSPECTED_FORGERY) negli ultimi 30 giorni">
        Sospetti 30gg
      </Tooltip>
    ),
    dataIndex: 'suspiciousCount30d',
    width: 120,
    align: 'right' as const,
    render: (v: number) => (
      <Tag color={v >= 2 ? brand.danger : brand.warn}>{v}</Tag>
    ),
  },
  {
    title: 'Ultimo evento',
    dataIndex: 'lastEventAt',
    width: 150,
    render: (v: string) =>
      v ? (
        dayjs(v).format('DD/MM/YY HH:mm')
      ) : (
        <Typography.Text type="secondary">—</Typography.Text>
      ),
  },
  {
    title: 'Scontrini',
    key: 'actions',
    width: 110,
    render: () => (
      <Link to="/receipts">
        <Typography.Text style={{ fontSize: 12, color: brand.secondary }}>
          Vedi tutti
        </Typography.Text>
      </Link>
    ),
  },
];

// ---------------------------------------------------------------------------
// Tabella eventi sospetti recenti
// ---------------------------------------------------------------------------

const EVENT_COLUMNS = [
  {
    title: 'Data',
    dataIndex: 'createdAt',
    width: 140,
    render: (v: string) => dayjs(v).format('DD/MM/YY HH:mm'),
  },
  {
    title: 'Tifoso',
    dataIndex: 'userEmail',
    render: (v: string) => (
      <Typography.Text style={{ fontSize: 12 }}>{v ?? '—'}</Typography.Text>
    ),
  },
  {
    title: 'Tipo',
    dataIndex: 'type',
    width: 160,
    render: (v: ViolationType) => <ViolationChip type={v} />,
  },
  {
    title: 'Motivo',
    dataIndex: 'reason',
    render: (v: string | null) =>
      v ? (
        <Typography.Text style={{ fontSize: 12 }}>{v}</Typography.Text>
      ) : (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          —
        </Typography.Text>
      ),
  },
  {
    title: 'Scontrino',
    dataIndex: 'receiptId',
    width: 160,
    render: (v: string | null) =>
      v ? (
        <Tooltip title="Vai alla lista scontrini — cerca per ID">
          <Link to="/receipts">
            <Typography.Text
              copyable={{ text: v }}
              style={{ fontFamily: 'monospace', fontSize: 11, color: brand.secondary }}
            >
              {v.slice(0, 8)}…
            </Typography.Text>
          </Link>
        </Tooltip>
      ) : (
        <Typography.Text type="secondary">—</Typography.Text>
      ),
  },
];

// ---------------------------------------------------------------------------
// Pagina principale
// ---------------------------------------------------------------------------

export function WatchlistPage(): React.ReactElement {
  const { data, isLoading } = useWatchlist();

  const counts = data?.counts ?? { flaggedUsers: 0, recentEvents: 0 };
  const flaggedUsers = data?.flaggedUsers ?? [];
  const recentEvents = data?.recentEvents ?? [];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      {/* Intestazione */}
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Sospetti / Watchlist
        </Typography.Title>
        <Typography.Text type="secondary">
          Tifosi probabilmente in frode e violazioni antifrode recenti.
        </Typography.Text>
      </div>

      {/* Banner didattico */}
      <Alert
        type="warning"
        showIcon
        icon={<ExclamationCircleOutlined />}
        style={{ marginBottom: 24 }}
        message="Come funziona la watchlist"
        description={
          <Space direction="vertical" size={4}>
            <Typography.Text style={{ fontSize: 13 }}>
              Un tifoso compare qui quando il sistema antifrode rileva{' '}
              <strong>scontrini probabilmente artefatti</strong>: immagini generate da AI (
              <Tag color={brand.primaryDeep} style={{ fontSize: 10 }}>AI generato</Tag>),
              scontrini con bassa autenticità visiva (
              <Tag color={brand.danger} style={{ fontSize: 10 }}>Falso sospetto</Tag>), o con
              leggibilità alta ma veridicità bassa (
              <Tag color={brand.warn} style={{ fontSize: 10 }}>Disparità R/V</Tag>).
            </Typography.Text>
            <Typography.Text style={{ fontSize: 13 }}>
              Al <strong>secondo evento sospetto in 30 giorni</strong> il tifoso viene marcato{' '}
              <Tag color={brand.danger} style={{ fontSize: 10 }}>HIGH</Tag> e tutti i suoi
              scontrini successivi vengono messi in <em>revisione manuale</em> automaticamente,
              indipendentemente dal verdetto AI.
            </Typography.Text>
          </Space>
        }
      />

      {/* Statistiche */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Tifosi flaggati (riskLevel HIGH)"
              value={counts.flaggedUsers}
              prefix={<UserOutlined style={{ color: brand.danger }} />}
              valueStyle={{ color: counts.flaggedUsers > 0 ? brand.danger : brand.win }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Violazioni sospette recenti"
              value={counts.recentEvents}
              prefix={<WarningOutlined style={{ color: brand.warn }} />}
              valueStyle={{ color: counts.recentEvents > 0 ? brand.warn : brand.win }}
            />
          </Card>
        </Col>
      </Row>

      {/* Tabella tifosi a rischio */}
      <Card
        title={
          <Space>
            <Typography.Text strong>Tifosi a rischio</Typography.Text>
            <Tag color={flaggedUsers.length > 0 ? 'red' : 'default'}>
              {flaggedUsers.length}
            </Tag>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {flaggedUsers.length === 0 && !isLoading ? (
          <Alert
            type="success"
            showIcon
            message="Nessun tifoso flaggato — tutto regolare"
            description="Nessun utente ha accumulato abbastanza violazioni da essere marcato HIGH risk."
          />
        ) : (
          <Table<WatchlistUser>
            rowKey="userId"
            size="small"
            loading={isLoading}
            dataSource={flaggedUsers}
            scroll={{ x: 'max-content' }}
            columns={FLAGGED_COLUMNS}
            pagination={flaggedUsers.length > 20 ? { pageSize: 20 } : false}
          />
        )}
      </Card>

      {/* Tabella eventi sospetti recenti */}
      <Card
        title={
          <Space>
            <Typography.Text strong>Violazioni sospette recenti</Typography.Text>
            <Tag color={recentEvents.length > 0 ? 'orange' : 'default'}>
              {recentEvents.length}
            </Tag>
          </Space>
        }
      >
        {recentEvents.length === 0 && !isLoading ? (
          <Alert
            type="success"
            showIcon
            message="Nessun evento sospetto recente — tutto regolare"
            description="Nessuna violazione antifrode registrata nel periodo recente."
          />
        ) : (
          <Table<WatchlistEvent>
            rowKey="id"
            size="small"
            loading={isLoading}
            dataSource={recentEvents}
            columns={EVENT_COLUMNS}
            pagination={recentEvents.length > 20 ? { pageSize: 20 } : false}
          />
        )}
      </Card>
    </div>
  );
}
